"use client"

import * as React from "react"
import { Input } from "@/components/ui/input"
import { useFormatters } from "@/hooks/use-formatters"
import { useNumericInput } from "@/hooks/use-numeric-input"

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'readOnly'> {
  /** The numeric value */
  value?: number | null
  /** Called when the numeric value changes */
  onChange?: (value: number | null) => void
  /** Currency code (e.g., 'CAD', 'USD'). If not provided, formats as number with 2 decimals */
  currency?: string
  /** Whether to allow negative values */
  allowNegative?: boolean
  /** Whether the input is read-only */
  readOnly?: boolean
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, currency, allowNegative = false, readOnly = false, ...props }, ref) => {
    const { formatCurrency } = useFormatters()
    
    // Use shared numeric input logic
    const {
      displayValue,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeyDown
    } = useNumericInput({
      value,
      decimalPlaces: 2,
      allowNegative,
      formatter: (val) => formatCurrency(val, currency)
    })

    return (
      <Input
        type="text"
        className={className}
        ref={ref}
        value={displayValue}
        readOnly={readOnly}
        {...props}
        {...(readOnly ? {} : {
          onChange: handleChange,
          onFocus: (e) => handleFocus(e, value, props.onFocus),
          onBlur: (e) => handleBlur(e, displayValue, onChange, props.onBlur),
          onKeyDown: (e) => handleKeyDown(e, props.onKeyDown)
        })}
      />
    )
  }
)
CurrencyInput.displayName = "CurrencyInput"

export { CurrencyInput }
